import React, { createContext, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
// WAS (backlog #160 öncesi): import {NODE_BACKEND_API_URL, NODE_BACKEND_API_PORT} from "../BackendConstants";
import { JAVA_BACKEND_API_URL, getAuthHeader } from "../BackendConstants";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

/**
 * E-posta Hesapları (system_email). Backlog #160 (2026-10-10): eski Node uçları
 * (gw.sakagrup.com/api/mail-settings, /api/users/search) ölüydü (404); artık FDS-Backend EmailController:
 *   GET    /email/system-emails?page&limit&search → { data, page, limit, total, totalPages }
 *   POST   /email/system-emails        PUT /email/system-emails/{id}        DELETE /email/system-emails/{id}
 *   GET    /email/users/search?search= → [{ user_id, user_name, user_surname, user_email }]
 * Okumalar personel; yazmalar SYSTEM_SETTINGS_MANAGE (Admin / SuperAdmin).
 *
 * ⚠️ Şifre YALNIZCA YAZILIR: hiçbir okuma system_email_password dönmüyor, yalnızca has_password.
 * Düzenlemede şifre boş bırakılırsa kayıtlı şifre korunur.
 *
 * Hata durumunda fonksiyonlar artık hatayı FIRLATIYOR (eskiden yutuluyordu ve modal "başarılı" gibi
 * kapanıyordu); mesaj { message } olarak sayfaya gidiyor.
 */
const MailSettingsContext = createContext();

function errorMessage(error, fallback) {
    const status = error?.response?.status;
    if (status === 401) return i18n.t("components:mailSettingsContext.oturumGecersiz401LutfenYeniden");
    if (status === 403) return error?.response?.data?.message || i18n.t("components:mailSettingsContext.buIslemIcinYetkinizYok");
    return error?.response?.data?.message || error?.message || fallback;
}

export const MailSettingsProvider = ({ children }) => {
  const { t } = useTranslation("components");
    const [mailSettings, setMailSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // WAS: mailSettings: `${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/mail-settings`
    const api = useMemo(() => ({
        mailSettings: `${JAVA_BACKEND_API_URL}/email/system-emails`,
        userSearch: `${JAVA_BACKEND_API_URL}/email/users/search`,
    }), []);

    const fetchMailSettings = useCallback(async (page = 1, limit = 10, search = '') => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await axios.get(api.mailSettings, {
                params: { page, limit, search },
                headers: getAuthHeader(),
            });
            setMailSettings(response.data);
        } catch (error) {
            console.error('Error fetching mail settings:', error);
            setLoadError(errorMessage(error, i18n.t("components:mailSettingsContext.ePostaHesaplariYuklenemedi")));
        } finally {
            setLoading(false);
        }
    }, [api]);

    // WAS: hatayı yutuyor, yanıtı listeye ekliyor ve aynı anda listeyi yeniden çekiyordu.
    const addMailSetting = async (newMailSetting) => {
        try {
            const response = await axios.post(api.mailSettings, newMailSetting, { headers: getAuthHeader() });
            return response.data;
        } catch (error) {
            console.error('Error adding mail setting:', error);
            throw new Error(errorMessage(error, t("mailSettingsContext.ePostaHesabiEklenemedi")));
        }
    };

    const updateMailSetting = async (id, updatedMailSetting) => {
        try {
            const response = await axios.put(`${api.mailSettings}/${id}`, updatedMailSetting, { headers: getAuthHeader() });
            return response.data;
        } catch (error) {
            console.error('Error updating mail setting:', error);
            throw new Error(errorMessage(error, t("mailSettingsContext.ePostaHesabiGuncellenemedi")));
        }
    };

    const deleteMailSetting = async (id) => {
        try {
            await axios.delete(`${api.mailSettings}/${id}`, { headers: getAuthHeader() });
            setMailSettings((prevSettings) => (
                Array.isArray(prevSettings?.data)
                    ? { ...prevSettings, data: prevSettings.data.filter((setting) => setting.system_email_id !== id) }
                    : prevSettings
            ));
        } catch (error) {
            console.error('Error deleting mail setting:', error);
            throw new Error(errorMessage(error, t("mailSettingsContext.ePostaHesabiSilinemedi")));
        }
    };

    const searchMailUsers = useCallback(async (searchTerm = "") => {
        if (!searchTerm || typeof searchTerm !== "string" || searchTerm.trim().length < 2) return [];

        try {
            // WAS: axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/users/search`, { params: { search: searchTerm } })
            const response = await axios.get(api.userSearch, { params: { search: searchTerm }, headers: getAuthHeader() });
            return response.data || [];
        } catch (error) {
            console.error("Kullanıcı ararken hata oluştu:", error);
            return [];
        }
    }, [api]);


    return (
        <MailSettingsContext.Provider value={{
            mailSettings,
            fetchMailSettings,
            addMailSetting,
            updateMailSetting,
            deleteMailSetting,
            searchMailUsers,
            loading,
            loadError,
        }}>
            {children}
        </MailSettingsContext.Provider>
    );
};

export default MailSettingsContext;

// ══ WAS (until 2026-10-10, backlog #160): the whole original file, calling the dead Node backend. ══
// Kept commented per the no-delete rule; the live code above replaces it.
// import React, { createContext, useState, useCallback, useMemo } from 'react';
// import axios from 'axios';
// import {NODE_BACKEND_API_URL, NODE_BACKEND_API_PORT} from "../BackendConstants";
//
// const MailSettingsContext = createContext();
//
// export const MailSettingsProvider = ({ children }) => {
//     const [mailSettings, setMailSettings] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [userOptions, setUserOptions] = useState([]);
//
//     const api = useMemo(() => ({
//         mailSettings: `${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/mail-settings`
//     }), []);
//
//     const fetchMailSettings = useCallback(async (page = 1, limit = 10, search = '') => {
//         setLoading(true);
//         try {
//             const response = await axios.get(api.mailSettings, {
//                 params: { page, limit, search }
//             });
//             setMailSettings(response.data);
//         } catch (error) {
//             console.error('Error fetching mail settings:', error);
//         } finally {
//             setLoading(false);
//         }
//     }, [api]);
//
//     const addMailSetting = async (newMailSetting) => {
//         try {
//             const response = await axios.post(api.mailSettings, newMailSetting);
//
//             // Önce mevcut tabloyu güncellemek için mail settings verilerini yeniden çek
//             fetchMailSettings();
//     
//             // Alternatif olarak eklenen öğeyi tabloya doğrudan ekleyebilirsiniz
//             setMailSettings((prevSettings) => 
//                 Array.isArray(prevSettings)
//                     ? [...prevSettings, response.data]
//                     : [response.data]
//             );
//         } catch (error) {
//             console.error('Error adding mail setting:', error);
//         }
//     };
//     
//
//     const updateMailSetting = async (id, updatedMailSetting) => {
//         try {
//             console.log("Updating mail setting with data:", updatedMailSetting); // Güncellenen veriyi kontrol edin
//             await axios.put(`${api.mailSettings}/${id}`, updatedMailSetting);
//             fetchMailSettings();
//         } catch (error) {
//             console.error('Error updating mail setting:', error);
//         }
//     };
//
//
//     const deleteMailSetting = async (id) => {
//         try {
//             await axios.delete(`${api.mailSettings}/${id}`);
//             setMailSettings((prevSettings) => {
//                 if (Array.isArray(prevSettings.data)) {
//                     return {
//                         ...prevSettings,
//                         data: prevSettings.data.filter((setting) => setting.system_email_id !== id),
//                     };
//                 } else {
//                     console.error("prevSettings is not an array:", prevSettings);
//                     return prevSettings; // hata durumunda orijinal değeri koru
//                 }
//             });
//         } catch (error) {
//             console.error('Error deleting mail setting:', error);
//         }
//     };
//
//     const searchMailUsers = useCallback(async (searchTerm = "") => {
//         if (!searchTerm || typeof searchTerm !== "string") return []; // Geçersiz girişler için boş array döndür
//
//         try {
//             const response = await axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/users/search`, { params: { search: searchTerm } });
//             return response.data || []; // Eğer data null veya undefined ise boş array döndür
//         } catch (error) {
//             console.error("Kullanıcı ararken hata oluştu:", error);
//             return []; // Hata durumunda boş array döndür
//         }
//     }, []);
//
//
//     return (
//         <MailSettingsContext.Provider value={{
//             mailSettings,
//             fetchMailSettings,
//             addMailSetting,
//             updateMailSetting,
//             deleteMailSetting,
//             searchMailUsers,
//             loading
//         }}>
//             {children}
//         </MailSettingsContext.Provider>
//     );
// };
//
// export default MailSettingsContext;
